import { View, Text, StyleSheet, ScrollView, Pressable, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { ArrowLeft } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { AccessibilityButton } from '@/components/AccessibilityButton';

export default function TermsOfServiceScreen() {
  const router = useRouter();

  return (
    <View style={styles.container}>
      {/* SEO/GEO FIX: add page-level canonical metadata for legal pages included in sitemap. */}
      {Platform.OS === 'web' && (
        <Head>
          <title>תנאי שימוש | Kursi</title>
          <meta name="description" content="תנאי השימוש של Kursi: זכויות, חובות, שימוש מותר והגבלות אחריות בפלטפורמה." />
          <meta name="robots" content="index,follow" />
          <link rel="canonical" href="https://kursiaii.com/legal/terms" />
          <meta property="og:title" content="תנאי שימוש | Kursi" />
          <meta property="og:description" content="תנאי השימוש של Kursi: זכויות, חובות, שימוש מותר והגבלות אחריות בפלטפורמה." />
          <meta property="og:url" content="https://kursiaii.com/legal/terms" />
        </Head>
      )}

      <View style={styles.header}>
        <Pressable onPress={() => router.back()} style={styles.backButton}>
          <ArrowLeft color={theme.colors.text} size={24} />
        </Pressable>
        <Text style={styles.headerTitle}>תנאי שימוש</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <Text style={styles.title}>תנאי שימוש</Text>
        <Text style={styles.updateDate}>עודכן לאחרונה: ינואר, 2026</Text>

        <View style={styles.section}>
          <Text style={styles.paragraph}>
            ברוכים הבאים ל-"קורסי" - פלטפורמה שמספקת שירות טכנולוגי לאיתור, סינון, דירוג והתאמה של קורסים והכשרות מקצועיות, באמצעות מערכת מבוססת בינה מלאכותית (להלן: "הפלטפורמה"), המופעלת על-ידי השותפות קורסי (להלן: "המפעילים").
          </Text>
          <Text style={styles.paragraph}>
            תנאי שימוש אלה מסדירים את הגישה והשימוש בפלטפורמה וכל הפונקציות והתכנים הנלווים אליה ("השירות"). תנאים אלה חלים עליכם ("אתה"/"משתמש") ונכנסים לתוקף מיד עם השימוש בפלטפורמה. במידה ואינך מקבל את תנאים אלה, אנא הפסק לגשת או להשתמש בפלטפורמה.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>הסכמה לתנאי השימוש ומדיניות הפרטיות</Text>

          <Text style={styles.subsectionTitle}>1.1</Text>
          <Text style={styles.paragraph}>
            כל שימוש בפלטפורמה, מותנה במפורש בהסכמה לתנאי השימוש, וכן למדיניות הפרטיות (להלן: "תנאי השימוש"). תנאי השימוש מהווים הסכם משפטי מחייב בין המשתמש לבין המפעילים והשימוש בפלטפורמה מהווה הצהרה והסכמה מלאה וללא תנאי להיות מחויב משפטית לתנאי השימוש.
          </Text>
          <Text style={styles.paragraph}>
            המשתמש שאינו מסכים לתנאים אלה, אינו רשאי לעשות כל שימוש בפלטפורמה ו/או בשירות.
          </Text>

          <Text style={styles.subsectionTitle}>1.2</Text>
          <Text style={styles.paragraph}>
            המפעילים עשויים מעת לעת לעדכן או לשנות את תנאי השימוש כדי לשקף שינויים בעסקי המפעילים, בחוק החל או מסיבות אחרות שייחשבו נחוצות על ידי המפעילים. שינויים כאלה יהיו מחייבים מבחינה משפטית כאשר יפורסמו בפלטפורמה, לכן מומלץ לעיין בתנאי השימוש מעת לעת לשינויים. המשך שימוש בפלטפורמה, לאחר שתנאי השימוש המתוקנים פורסמו בפלטפורמה, מעיד על הסכמת המשתמש להיות מחויב משפטית לשינויים כאמור.
          </Text>

          <Text style={styles.subsectionTitle}>1.3</Text>
          <Text style={styles.paragraph}>
            כל מקרה של סתירה בין הוראות תנאי שימוש זה לבין מידע המתקבל ממקור אחר, יגברו הוראות תקנון זה.
          </Text>

          <Text style={styles.subsectionTitle}>1.4</Text>
          <Text style={styles.paragraph}>
            תנאי השימוש מנוסחים בלשון זכר מטעמי נוחות בלבד והם מתייחסים באופן שווה לנשים ולגברים.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>2.	שימוש בפלטפורמה</Text>

          <Text style={styles.subsectionTitle}>2.1</Text>
          <Text style={styles.paragraph}>
            ככל אדם, לרבות תאגיד, רשאי לעשות שימוש בפלטפורמה ולקבל את השירותים המוצעים בה, לרבות קבלת המלצות לקורסים, ובלבד שהמשתמש מקבל על עצמו את תנאי שימוש אלו במלואם ומתקיימים בו כל התנאים המצטברים שלהלן:
          </Text>
          <Text style={styles.listItem}>	2.1.1	המשתמש הינו בעל כשרות משפטית לבצע פעולות משפטיות מחייבות, ומלאו לו 16 שנים לפחות;</Text>
          <Text style={styles.listItem}>	2.1.2	משתמש מסר פרטים נכונים, מלאים ומדויקים ככל שנדרש לצורך קבלת השירותים בפלטפורמה, ובפרט ככל שבחר למסור את פרטיו לצורך העברתם לספקי קורסים כפי שמפורט בהמשך;</Text>
          <Text style={styles.listItem}>	2.1.3	ההמשתמש מאשר כי קרא והבין את תנאי השימוש, לרבות מדיניות הפרטיות, והסכים להם במפורש;</Text>

          <Text style={styles.subsectionTitle}>2.2</Text>
          <Text style={styles.paragraph}>
            ההמשתמש מודע לכך שהפלטפורמה אינה ספקית קורסים, אינה צד להתקשרות עם ספקי הקורסים, וכי העברת פרטיו לספק קורס תיעשה אך ורק בהסכמתו המפורשת של המשתמש;
          </Text>

          <Text style={styles.subsectionTitle}>2.3</Text>
          <Text style={styles.paragraph}>
            המפעילים יהיו רשאים, על פי שיקול דעתם הבלעדי וללא חובת מתן הודעה מוקדמת, למנוע ממשתמש את הגישה לפלטפורמה, כולה או חלקה, באופן זמני או לצמיתות, וכן להגביל את השימוש בשירותים, כולם או מקצתם.
          </Text>

          <Text style={styles.subsectionTitle}>2.4</Text>
          <Text style={styles.paragraph}>
            בלי לגרוע מהאמור בסעיף 2.3 לעיל, המפעילים יהיו רשאים למנוע מהמשתמש לעשות שימוש בפלטפורמה או לבטל העברת פרטים לספקי קורסים, בכל אחד מהמקרים הבאים: (א) אם המשתמש מסר במתכוון או ברשלנות פרטים שגויים, כוזבים, מטעים או חסרים; (ב) אם המשתמש עשה שימוש בלתי חוקי, פוגעני, מטעה או לרעה בפלטפורמה או במערכותיה, לרבות ניסיון לפגוע באבטחת המידע, בפעילות המערכת, במוניטין המפעיל, בקניינו הרוחני או בצדדים שלישיים; (ג) אם המשתמש הפר תנאי מתנאי שימוש אלה או פעל בניגוד לכל דין; (ד) אם קיים חשש סביר כי השימוש של המשתמש בפלטפורמה עלול לגרום נזק למפעיל, למשתמשים אחרים או לספקי הקורסים.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>3.	הגבלות משתמשים</Text>

          <Text style={styles.subsectionTitle}>3.1</Text>
          <Text style={styles.paragraph}>
            בעת שימוש בפלטפורמה ו/או בשירות המשתמש מתחייב שלא לבצע את הפעולות הבאות:
          </Text>
          <Text style={styles.listItem}>	3.1.1	הונאה פיננסית או כספית, או מימון או הקלה של כל פעילות פלילית או בלתי חוקית אחרת;</Text>
          <Text style={styles.listItem}>3.1.2	התחזות לאדם או גוף כלשהו ו/או מסירת מידע על אדם אחר ללא הסכמתו כדין, או כל פגיעה אחרת בכל דרך שהיא בזכויות הפרטיות של אדם אחר;</Text>
          <Text style={styles.listItem}>3.1.3	מסירת פרטים שגויים בעת השימוש בפלטפורמה או עלאת מידע לפלטפורמה ללא אישור מתאים והרשאה כדין;</Text>
          <Text style={styles.listItem}>3.1.4	שינוי, עיבוד, העתקה, שאיבת מידע, פירוק או הרכבה מחדש של כל אחד מרכיבי הפלטפורמה;</Text>
          <Text style={styles.listItem}>3.1.5	שכפול, העתקה, פירוק והרכבה מחדש, מכירה או ניצול לכל מטרה מסחרית של שירותי המפעילים או כל חלק מהם, לרבות הצעה או מכירה של הזכות להשתמש בפלטפורמה או שירותים;</Text>
          <Text style={styles.listItem}>3.1.6	שיבוש או הפרעה לשירות, לשרתים תומכים או לרשתות, באופן ידני או באמצעות שימוש בסקריפטים, וירוסים או תולעים, או יצירות עומס יתר על המערכות שבהן משתמשים המפעילים על מנת להפעיל את הפלטפורמה ו/או לספק את השירות;</Text>
          <Text style={styles.listItem}>3.1.7	שידול בדוא"ל, הפצת "דואר זבל" או דייג (phishing);</Text>
          <Text style={styles.listItem}>3.1.8	פגיעה בכל דרך שהיא בזכויות יוצרים, סימני מסחר, או כל זכות קניינית אחרת של המפעילים או של כל צד שלישי;</Text>
          <Text style={styles.listItem}>3.1.9	כל פעילות אחרת האסורה על פי תנאים אלה או על פי החוק החל.</Text>

          <Text style={styles.subsectionTitle}>3.2</Text>
          <Text style={styles.paragraph}>
            המשתמש מסכים כי כל הפרה של תנאים כאמור בסעיף קטן 3.1, וגם שימוש בלתי מורשה בפלטפורמה, חומרי תוכן וקניין רוחני, כמו גם כל פעילות הונאה או פעילות בלתי חוקית אחרת, יהווה עילה לסיום זכות המשתמש לגשת, לגלוש ולהשתמש בפלטפורמה ובשירות. המפעילים שומרים לעצמם זכות לסיים או להשעות את הגישה של המשתמש לפלטפורמה, לתכניה ו/או לשירות ולשימוש בהם בכל עת, עם או בלי הודעה מוקדמת, לפי שיקול דעתם הבלעדי של המפעילים. במקרה של הפסקת הגישה של המשתמש לפלטפורמה, נאסר עליו לגשת לפלטפורמה תחת שם חדש, אלא הדבר אושר במפורש על ידי המפעילים.
          </Text>

          <Text style={styles.subsectionTitle}>3.3</Text>
          <Text style={styles.paragraph}>
            מבלי לגרוע מהאמור לעיל, מובהר כי המפעילים שומרים לעצמם את הזכות לתבוע כל משתמש במקרה של הפרת זכויות קניין רוחני ו/או שימוש בקניין רוחני שלם ללא קבלת אישור מראש ובכתב ו/או ללא מתן קרדיט כפי שאושר מראש על-ידי המפעילים.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>4.	תיאור השירות</Text>

          <Text style={styles.subsectionTitle}>4.1</Text>
          <Text style={styles.paragraph}>
            פלטפורמה מספקת שירות טכנולוגי מקוון, המבוסס על מערכות בינה מלאכותית, אשר נועד לאפשר למשתמשים לאתר, לסנן, לדרג ולקבל המלצות כלליות בנוגע לקורסים, הכשרות מקצועיות ותכניות לימוד שונות המוצעות על-ידי צדדים שלישיים (להלן: "ספקי הקורסים").
          </Text>

          <Text style={styles.subsectionTitle}>4.2</Text>
          <Text style={styles.paragraph}>
            לצורך מתן השירות, המשתמש יתבקש להשיב על שאלות המוצגות לו באמצעות סוכן בינה מלאכותית הפועל במסגרת הפלטפורמה, וזאת לצורך יצירת פרופיל התאמה אישי המבוסס על המידע שנמסר על-ידי המשתמש. על בסיס המידע שנמסר כאמור, תציג הפלטפורמה למשתמש רשימה של קורסים אשר, לפי הערכת המערכת, עשויים להתאים לצרכיו ולהעדפותיו, לרבות דירוג יחסי והמלצה כללית, והכול באופן אוטומטי וללא התערבות אנושית ישירה.
          </Text>

          <Text style={styles.subsectionTitle}>4.3</Text>
          <Text style={styles.paragraph}>
            מובהר ומוסכם בזאת כי הפלטפורמה אינה ספקית קורסים, אינה מפעילה קורסים, אינה אחראית לתוכנם, לאיכותם או לזמינותם, ואינה מהווה צד כלשהו להתקשרות בין המשתמש לבין מי מספקי הקורסים.
          </Text>

          <Text style={styles.subsectionTitle}>4.4</Text>
          <Text style={styles.paragraph}>
            מובהר ומוסכם בזאת כי הצגת הקורסים למשתמשים במסגרת הפלטפורמה נעשית באופן אנונימי, כך שבשלב הראשוני של הצגת תוצאות ההתאמה לא נחשפים שמותיהם, זהותם המסחרית או פרטיהם של ספקי הקורסים ו/או בעלי הקורסים.
          </Text>
          <Text style={styles.paragraph}>
            	הקורסים מוצגים למשתמש באמצעות תיאורים כלליים, מאפיינים מקצועיים, נתונים השוואתיים ודירוגים, אשר נוצרים באופן אוטומטי על-ידי מערכת מבוססת בינה מלאכותית (AI), על בסיס מידע המצוי במאגרי המידע של הפלטפורמה ו/או מידע פומבי שנאסף ממקורות גלויים, לרבות אתרי אינטרנט של ספקי הקורסים ומקורות ציבוריים נוספים.
          </Text>

          <Text style={styles.subsectionTitle}>4.5</Text>
          <Text style={styles.paragraph}>
            חשיפת זהותו של ספק הקורס ו/או פרטי ההתקשרות עמו תתבצע, ככל שתתבצע, אך ורק לאחר שהמשתמש נתן לכך הסכמה מפורשת, בהתאם למנגנון הקבוע בפלטפורמה ובהתאם להוראות תנאי שימוש אלה.
          </Text>

          <Text style={styles.subsectionTitle}>4.6</Text>
          <Text style={styles.paragraph}>
            המשתמש מאשר ומבין כי אופן הצגת הקורסים, ניסוח התיאורים, הדירוגים וההמלצות מבוססים על עיבוד אלגוריתמי אוטומטי של נתונים, וכי אין מדובר בהצגה מלאה של כלל פרטי הקורס או בהתחייבות כלשהי מצד הפלטפורמה בנוגע למהימנות, שלמות או התאמה אישית מושלמת של המידע המוצג.
          </Text>

          <Text style={styles.subsectionTitle}>4.7	שימוש חינמי וללא רישום</Text>
          <Text style={styles.listItem}>4.7.1	השימוש בפלטפורמה ובשירותים המוצעים במסגרתה הינו חינמי ואינו כרוך בתשלום כלשהו מצד המשתמש, אלא אם יצוין אחרת במפורש ובכתב על-ידי המפעילים.</Text>
          <Text style={styles.listItem}>4.7.2	צורך קבלת השירותים הבסיסיים בפלטפורמה, המשתמש אינו נדרש לפתוח חשבון משתמש, להירשם למערכת או להזדהות באמצעות פרטי התחברות קבועים כלשהם.</Text>
          <Text style={styles.listItem}>4.7.3	על אף האמור לעיל, המפעילים שומרים לעצמם את הזכות לאפשר בעתיד, לפי שיקול דעתם הבלעדי, התחברות ו/או זיהוי באמצעות שירותים של צדדים שלישיים, לרבות רשתות חברתיות או ספקי זהות דיגיטלית (כגון Google, Facebook, Apple וכיוצא באלה).</Text>
          <Text style={styles.listItem}>4.7.4	מובהר כי שימוש במנגנוני התחברות של צדדים שלישיים, ככל שיופעלו, יהיה כפוף לתנאי השימוש ולמדיניות הפרטיות של אותם צדדים שלישיים, וכי למפעיל לא תהיה כל אחריות לפעילותם, לזמינותם, לאבטחת המידע הנמסר להם או לאופן עיבודו על-ידם.</Text>
          <Text style={styles.listItem}>4.7.5	מבלי לגרוע מהאמור לעיל, המפעילים אינם מתחייבים כי אפשרות התחברות באמצעות שירותי צד שלישי תהיה זמינה בכל עת, ואינם אחראים לביטולה, לשינוייה או להגבלתה, מכל סיבה שהיא.</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>5.	מסירת פרטים והעברת פניות ("לידים")</Text>

          <Text style={styles.subsectionTitle}>5.1</Text>
          <Text style={styles.paragraph}>
            כל המשתמש רשאי, אך אינו חייב, לפי שיקול דעתו הבלעדי, לאשר למפעילי הפלטפורמה העברת פרטיו האישיים לספק קורס מסוים אשר הוצג לו באמצעות הפלטפורמה.
          </Text>

          <Text style={styles.subsectionTitle}>5.2</Text>
          <Text style={styles.paragraph}>
            לצורך העברת הפרטים כאמור, המשתמש יתבקש למלא טופס ייעודי במסגרת הפלטפורמה, הכולל, בין היתר, את שמו ומספר הטלפון שלו, ולסמן באופן ברור את הסכמתו להעברת פרטים אלה לספק קורס מסוים וליצירת קשר עמו בקשר לקורס שבו הביע עניין.
          </Text>

          <Text style={styles.subsectionTitle}>5.3</Text>
          <Text style={styles.paragraph}>
            המשתמש מאשר ומודע לכך שהפלטפורמה מקבלת תמורה כספית מספקי הקורסים בגין העברת פניות ו/או פרטי משתמשים המעוניינים בקורסים המוצעים על-ידם.
          </Text>

          <Text style={styles.subsectionTitle}>5.4</Text>
          <Text style={styles.paragraph}>
            מובהר ומוסכם בזאת כי הפלטפורמה לא תעביר פרטים אישיים מזהים של משתמשים כלשהם לספקי קורסים או לכל צד שלישי אחר, אלא לאחר קבלת הסכמה מפורשת, מודעת ואקטיבית של המשתמש הרלוונטי.
          </Text>

          <Text style={styles.subsectionTitle}>5.5</Text>
          <Text style={styles.paragraph}>
            המשתמש מאשר ומסכים בזאת כי עצם מתן ההסכמה להעברת פרטיו מהווה אישור מפורש לכך שספק הקורס הרלוונטי יהיה רשאי ליצור עמו קשר באמצעים סבירים, לרבות באמצעות שיחת טלפון, הודעת טקסט (SMS), הודעות ביישומי מסרים ו/או דואר אלקטרוני, והכול בהתאם להוראות כל דין.
          </Text>

          <Text style={styles.subsectionTitle}>5.6</Text>
          <Text style={styles.paragraph}>
            המשתמש מודע ומסכים לכך כי העברת פרטיו לספק הקורס מבוצעת לפי בקשתו ולצרכיו בלבד, וכי לאחר העברת הפרטים כאמור, האחריות לשמירה, לעיבוד, לאבטחה ולשימוש במידע האישי תחול על ספק הקורס, בהתאם למדיניות הפרטיות ולנהלים שלו.
          </Text>

          <Text style={styles.subsectionTitle}>5.7</Text>
          <Text style={styles.paragraph}>
            המפעיל לא יישא בכל אחריות לכל שימוש שיעשה ספק הקורס בפרטי המשתמש, לרבות פניות שיווקיות, אופן יצירת הקשר, תדירות הפניות, תוכנן או תוצאותיהן.
          </Text>

          <Text style={styles.subsectionTitle}>5.8</Text>
          <Text style={styles.paragraph}>
            המשתמש מאשר ומודע לכך שהפלטפורמה מקבלת תמורה כספית מספקי הקורסים בגין העברת פרטי המשתמשים המעוניינים בקורסים המוצעים על-ידם, וכי תמורה זו אינה מטילה על המפעילים כל אחריות נוספת כלפי המשתמש בקשר לקורסים או לספקיהם.
          </Text>

          <Text style={styles.subsectionTitle}>5.9</Text>
          <Text style={styles.paragraph}>
            המשתמש רשאי בכל עת לפנות ישירות לספק הקורס הרלוונטי ולדרוש הפסקת פניות ו/או מחיקת פרטיו ממאגרי המידע של הספק, בהתאם לזכויותיו על פי דין.
          </Text>

          <Text style={styles.subsectionTitle}>5.10</Text>
          <Text style={styles.paragraph}>
            מבלי לגרוע מהאמור לעיל, המשתמש מוותר מראש, במידה המרבית המותרת לפי דין, על כל טענה, דרישה או תביעה כלפי המפעילים בקשר לעצם העברת פרטיו, לתוצאותיה, או לכל התקשרות שנוצרה בעקבותיה, ובלבד שהעברת הפרטים נעשתה בהתאם להסכמתו המפורשת.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>6.	שימוש במידע ובינה מלאכותית – הצהרת ויתור (Disclaimer)</Text>

          <Text style={styles.subsectionTitle}>6.1</Text>
          <Text style={styles.paragraph}>
            מובהר בזאת כי מערכת ההתאמה וההמלצות הפועלת במסגרת הפלטפורמה מבוססת, בין היתר, על אלגוריתמים של בינה מלאכותית (AI), למידת מכונה ועיבוד מידע אוטומטי.
          </Text>

          <Text style={styles.subsectionTitle}>6.2</Text>
          <Text style={styles.paragraph}>
            משתמש מצהיר ומאשר כי ידוע לו והוא מסכים לכך כי:
          </Text>
          <Text style={styles.listItem}>6.2.1	ההמלצות, הדירוגים, הסיווגים והתכנים המוצגים בפלטפורמה מבוססים על עיבוד אוטומטי של נתונים והינם הסתברותיים בלבד;</Text>
          <Text style={styles.listItem}>6.2.2	ייתכנו טעויות, חוסרים, אי-דיוקים, עיכובים בעדכון מידע, הצגת מידע חלקי ו/או הטיות אלגוריתמיות;</Text>
          <Text style={styles.listItem}>6.2.3	המידע, ההמלצות והתכנים המוצגים בפלטפורמה אינם מהווים, ולא ייחשבו בשום מקרה, כייעוץ מקצועי, לימודי, פיננסי, תעסוקתי, משפטי או אחר, ואינם מהווים תחליף לבדיקה עצמאית או לייעוץ מקצועי מתאים;</Text>

          <Text style={styles.subsectionTitle}>6.3</Text>
          <Text style={styles.paragraph}>
            מובהר בזאת כי אין לראות בתוצאות ההתאמה, בהמלצות או בדירוגים משום התחייבות מצד המפעילים ו/או הפלטפורמה להתאמה מוצלחת, להצלחת המשתמש בלימודיו, לשביעות רצון מהקורסים או להשגת תוצאה כלשהי.
          </Text>

          <Text style={styles.subsectionTitle}>6.4</Text>
          <Text style={styles.paragraph}>
            האחריות הבלעדית והמלאה לבחינת הקורסים, ספקי הקורסים, תנאי ההתקשרות, המחירים, איכות התכנים, רמת ההוראה והתאמתם לצרכיו האישיים של המשתמש חלה על המשתמש בלבד.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>7.	היעדר אחריות לתוכן הקורסים ולספקי הקורסים</Text>

          <Text style={styles.subsectionTitle}>7.1</Text>
          <Text style={styles.paragraph}>
            מובהר ומוסכם בזאת כי הפלטפורמה, השירותים המוצעים במסגרתה וכל התכנים המופיעים בה מסופקים למשתמש במתכונת "כמות שהם" (AS IS) ו-"ככל שזמינים" (AS AVAILABLE), ובמידה המרבית המותרת על פי דין.
          </Text>

          <Text style={styles.subsectionTitle}>7.2</Text>
          <Text style={styles.paragraph}>
            מובהר כי הפלטפורמה אינה מפקחת על ספקי הקורסים, אינה בודקת את פעילותם ואינה אחראית, במישרין או בעקיפין, לכל אחד מאלה:
          </Text>
          <Text style={styles.listItem}>7.2.1	איכות הקורסים, תכניהם, היקפם או התאמתם לצורכי המשתמש;</Text>
          <Text style={styles.listItem}>7.2.2	רמת ההוראה, מקצועיות המרצים או צוותי ההדרכה;</Text>
          <Text style={styles.listItem}>7.2.3	אמינות, דיוק, שלמות או עדכניות המידע הנמסר על-ידי ספקי הקורסים;</Text>
          <Text style={styles.listItem}>7.2.4	קיום התחייבויות מצד ספקי הקורסים, לרבות מועדי פתיחה, מתכונת הלימודים, תעודות, השמות תעסוקתיות וכיוצא באלה;</Text>
          <Text style={styles.listItem}>7.2.5	התנהלות מסחרית, פיננסית, חוזית או משפטית של ספקי הקורסים;</Text>
          <Text style={styles.listItem}>7.2.6	כל נזק, הפסד או הוצאה, ישירים או עקיפים, הנובעים מהתקשרות בין המשתמש לבין ספק קורס כלשהו.</Text>

          <Text style={styles.subsectionTitle}>7.3</Text>
          <Text style={styles.paragraph}>
            מובהר כי מרבית המידע והתכנים המוצגים בפלטפורמה, לרבות תיאורי קורסים, פרטים אודותיהם, דירוגים, תמונות ונתונים נוספים, מקורם בספקי הקורסים ו/או במקורות חיצוניים, ומוצגים כשירות למשתמש בלבד. הפלטפורמה אינה מתחייבת לדיוקם, לשלמותם או למהימנותם של תכנים אלו.
          </Text>

          <Text style={styles.subsectionTitle}>7.4</Text>
          <Text style={styles.paragraph}>
            למשתמש לא תהיה כל טענה, דרישה או תביעה כלפי המפעילים ו/או מי מטעמם בקשר לתוכן הקורסים, לאיכותם, לנתוניהם, לאופן הצגתם בפלטפורמה או לכל התקשרות שביצע עם ספק קורס.
          </Text>

          <Text style={styles.subsectionTitle}>7.5</Text>
          <Text style={styles.paragraph}>
            כל התקשרות בין המשתמש לבין ספק קורס, לרבות מסירת פרטים אישיים, רישום לקורס, תשלום או השתתפות בפועל, נעשית באחריותו הבלעדית והמלאה של המשתמש.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>8.	הגבלת אחריות</Text>

          <Text style={styles.subsectionTitle}>8.1</Text>
          <Text style={styles.paragraph}>
            במידה המרבית המותרת לפי כל דין, המפעילים, בעלי מניותיה בפלטפורמה, מנהליה, עובדיה, שלוחיה, ספקיה ושותפיה לא יישאו בכל אחריות לכל נזק מכל סוג שהוא, לרבות אך לא רק:
          </Text>
          <Text style={styles.listItem}>8.1.1	אובדן הכנסות או רווחים;</Text>
          <Text style={styles.listItem}>8.1.2	פגיעה במוניטין או בשם הטוב;</Text>
          <Text style={styles.listItem}>8.1.3	עוגמת נפש או נזק לא ממוני;</Text>
          <Text style={styles.listItem}>8.1.4	נזק עקיף, תוצאתי, מקרי או מיוחד;</Text>
          <Text style={styles.listItem}>8.1.5	נזק הנובע מהסתמכות על מידע, המלצות, דירוגים או תכנים המופיעים בפלטפורמה;</Text>
          <Text style={styles.listItem}>8.1.6	החלטות שקיבל המשתמש על בסיס השירותים או המידע שסופקו לו באמצעות הפלטפורמה.</Text>

          <Text style={styles.subsectionTitle}>8.2</Text>
          <Text style={styles.paragraph}>
            בלי לגרוע מהאמור לעיל, אחריותו הכוללת והמצטברת של המפעילים, אם וככל שתיקבע על-ידי ערכאה מוסמכת, לא תעלה בכל מקרה על הסכום ששולם בפועל על-ידי המשתמש לפלטפורמה, אם שולם (וככלל – אפס).
          </Text>

          <Text style={styles.subsectionTitle}>8.3</Text>
          <Text style={styles.paragraph}>
            המשתמש מאשר ומסכים כי מגבלות האחריות המפורטות לעיל מהוות תנאי יסודי להסכמת המפעילים להעמיד לרשותו את הפלטפורמה והשירותים הכלולים בה.
          </Text>

          <Text style={styles.subsectionTitle}>8.4</Text>
          <Text style={styles.paragraph}>
            המפעילים אינם מתחייבים כי פעילות הפלטפורמה תהיה רציפה, ללא תקלות, הפרעות או שגיאות, ואינם אחראים לנזקים הנובעים מתקלות טכניות, כשלים בתקשורת, עומסים, תחזוקה, מתקפות סייבר, וירוסים או גורמים חיצוניים אחרים.
          </Text>

          <Text style={styles.subsectionTitle}>8.5</Text>
          <Text style={styles.paragraph}>
            המפעילים אינם מתחייבים כי מערכות הפלטפורמה, שרתיה או כל מידע המועבר באמצעותה יהיו נקיים מתקלות, וירוסים או רכיבים מזיקים אחרים, ואינם נושאים באחריות לאבטחה מוחלטת של מידע המועבר דרך רשת האינטרנט.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>9.	פרטיות ושימוש במידע אישי</Text>

          <Text style={styles.subsectionTitle}>9.1</Text>
          <Text style={styles.paragraph}>
            המפעילים מתחייבים לפעול בשקידה סבירה לשמירה על פרטיות המשתמשים ועל הגנת המידע האישי הנמסר במסגרת השימוש בפלטפורמה, בהתאם להוראות כל דין החל, לרבות חוק הגנת הפרטיות, התשמ"א–1981, והתקנות מכוחו.
          </Text>

          <Text style={styles.subsectionTitle}>9.2</Text>
          <Text style={styles.paragraph}>
            מידע אישי שיימסר על-ידי המשתמש, לרבות שם, מספר טלפון, ופרטים נוספים שיימסרו מרצונו במסגרת השימוש בפלטפורמה או לצורך העברת פנייה לספק קורס, ישמש לצרכים הבאים בלבד:
          </Text>
          <Text style={styles.listItem}>	9.2.1	מתן השירותים המוצעים בפלטפורמה;</Text>
          <Text style={styles.listItem}>9.2.2	יצירת קשר עם המשתמש, ככל שנדרש לצורך השירות;</Text>
          <Text style={styles.listItem}>9.2.3	העברת פרטים לספקי קורסים – וזאת אך ורק לאחר קבלת הסכמה מפורשת של המשתמש ובהתאם לסעיף 5 לעיל;</Text>
          <Text style={styles.listItem}>9.2.4	שיפור, בקרה וניתוח סטטיסטי של פעילות הפלטפורמה (לרבות באמצעות מידע מצרפי ו/או אנונימי).</Text>

          <Text style={styles.subsectionTitle}>9.3</Text>
          <Text style={styles.paragraph}>
            הפרטים שיימסרו על-ידי המשתמש ומידע נוסף שיצטבר אודותיו במהלך השימוש בפלטפורמה יישמרו במאגרי המידע של המפעילים או מי מטעמם, וייעשה בהם שימוש אך ורק בהתאם להוראות תנאי שימוש אלו, למדיניות הפרטיות של הפלטפורמה (ככל שפורסמה) ולהוראות כל דין.
          </Text>

          <Text style={styles.subsectionTitle}>9.4</Text>
          <Text style={styles.paragraph}>
            מבלי לגרוע מהאמור לעיל, המשתמש מודע ומסכים לכך כי המפעילים אינם יכולים להבטיח חסינות מוחלטת מפני חדירות, כשלים טכנולוגיים, גישה בלתי מורשית או אובדן מידע, והם לא יישאו באחריות לכל נזק, ישיר או עקיף, שייגרם עקב אובדן מידע, שימוש בלתי מורשה בו או חשיפתו, למעט במקרים בהם הדבר נדרש במפורש על פי דין.
          </Text>

          <Text style={styles.subsectionTitle}>9.5</Text>
          <Text style={styles.paragraph}>
            המשתמש אחראי לנקוט באמצעי זהירות סבירים בשימוש בפלטפורמה, ומומלץ לו להימנע ממסירת מידע רגיש שאינו נדרש לשם קבלת השירות.
          </Text>

          <Text style={styles.subsectionTitle}>9.6</Text>
          <Text style={styles.paragraph}>
            בעצם השימוש בפלטפורמה, המשתמש נותן את הסכמתו המפורשת לאיסוף, לשמירה, לעיבוד ולשימוש במידע האישי שלו בהתאם לתנאי שימוש אלו ולמדיניות הפרטיות של הפלטפורמה, כפי שתעודכן מעת לעת.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>10.	קניין רוחני וזכויות יוצרים</Text>

          <Text style={styles.subsectionTitle}>10.1</Text>
          <Text style={styles.paragraph}>
            המשתמש מאשר ומסכים בזאת כי כל הזכויות מכל סוג שהוא בקשר לפלטפורמה, למערכותיה, לעיצובה, לממשק המשתמש, לאלגוריתמים, למודלים של הבינה המלאכותית, לקוד המקור, לקוד הבינארי, למסדי הנתונים, למבנה המידע, לשיטות הפעולה, ולכל תוכן אחר שפותח ו/או נוצר על-ידי המפעיל או מי מטעמו (לרבות, אך לא רק, טקסטים, גרפיקה, תמונות, קטעי שמע, סרטונים, סמלים, כפתורים, אייקונים, עיצובים, תרשימים, הורדות דיגיטליות ותוכנות מחשב) (להלן: "קניין רוחני של המפעילים"), הינן רכושו הבלעדי של המפעילים ו/או של שותפיהם וספקיהם, ומוגנות על-ידי דיני זכויות היוצרים, סימני המסחר, סודות מסחריים וכל דין רלוונטי אחר בישראל ובעול.
          </Text>

          <Text style={styles.subsectionTitle}>10.2</Text>
          <Text style={styles.paragraph}>
            בלי לגרוע מהאמור לעיל, כל סימן מסחרי, שם מסחרי, לוגו, סימן שירות, שם מתחם (Domain), עיצוב מסחרי וכל סימן מזהה אחר המופיע בפלטפורמה בקשר עם המפעילים, מהווים קניין רוחני מוגן של המפעילים ו/או בעלי הזכויות אחרים בפלטפורמה.
          </Text>

          <Text style={styles.subsectionTitle}>10.3</Text>
          <Text style={styles.paragraph}>
            המשתמש אינו רשאי להעתיק, לשכפל, להפיץ, לפרסם, להעמיד לרשות הציבור, לבצע הנדסה לאחור, לשנות, לעבד, לתרגם, למכור, להשכיר, להעמיד ברישיון משנה, או לעשות כל שימוש מסחרי או אחר בקניין הרוחני של המפעיל, כולו או חלקו, אלא אם קיבל לכך אישור מפורש, מראש ובכתב, מהמפעילים.
          </Text>

          <Text style={styles.subsectionTitle}>10.4</Text>
          <Text style={styles.paragraph}>
            קניין רוחני של ספקי הקורסים. מובהר ומוסכם בזאת כי כל הזכויות בתכנים הנוגעים לקורסים המוצגים בפלטפורמה, לרבות שמות הקורסים, תיאוריהם, תכניהם, סילבוסים, חומרי לימוד, מצגות, סרטונים, תמונות, סימנים מסחריים, לוגואים, שמות מסחריים, ומידע אחר הקשור לספקי הקורסים (להלן: "קניין רוחני של ספקי הקורסים"), שייכות באופן בלעדי לספקי הקורסים ו/או לבעלי הזכויות בהם, לפי העניין.
          </Text>
          <Text style={styles.paragraph}>
            	הצגת תכנים של ספקי הקורסים בפלטפורמה נעשית באישורם או בהתאם להרשאות שניתנו למפעילים, אך אין בה כדי להקנות למשתמש כל זכות קניינית או רישיון כלשהו להשתמש בתכנים אלו מעבר לצפייה אישית ולא מסחרית במסגרת השימוש בפלטפורמה.
          </Text>
          <Text style={styles.paragraph}>
            	המשתמש מתחייב שלא לעשות כל שימוש בתכני ספקי הקורסים, לרבות העתקה, הפצה, פרסום, שינוי או הצגה פומבית, ללא קבלת אישור מראש ובכתב מבעלי הזכויות הרלוונטיים.
          </Text>

          <Text style={styles.subsectionTitle}>10.5</Text>
          <Text style={styles.paragraph}>
            קישורים ושימוש בסימנים מסחריים. המפעילים אינם מעניקים, במפורש או במשתמע, כל רישיון או זכות שימוש בקניינו הרוחני, לרבות בסימני המסחר, בלוגו או בעיצובים הקנייניים שלו, ולא ניתן לעשות בהם שימוש כלשהו, לרבות לצורך יצירת קישור (Link) לפלטפורמה, מיתוג, פרסום או קידום, ללא קבלת אישור מפורש ומראש ובכתב מהמפעיל.
          </Text>

          <Text style={styles.subsectionTitle}>10.6</Text>
          <Text style={styles.paragraph}>
            כל קניין רוחני שאינו בבעלות המפעיל והמופיע בפלטפורמה הינו רכושם של בעליו החוקיים, והשימוש בו כפוף לתנאים שנקבעו על-ידי אותם בעלי זכויות.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>11.	אחריות לגבי קישורים, תכנים ושירותים של צדדים שלישיים</Text>

          <Text style={styles.subsectionTitle}>11.1</Text>
          <Text style={styles.paragraph}>
            הפלטפורמה עשויה לכלול קישורים, מידע או תוכן המסופקים על ידי צדדים שלישיים. מובהר כי מפעילי הפלטפורמה אינם מפעילים, מפקחים או מנתרים אתרי אינטרנט ותכנים אלו, והם אינם נושאים באחריות כלשהי לתקינותם של הקישורים, האתרים, המוצרים, המאמרים, המידע, התוכן או השירותים המוצעים במסגרתם.
          </Text>

          <Text style={styles.subsectionTitle}>11.2</Text>
          <Text style={styles.paragraph}>
            מובהר בזאת כי ציון שם, הצגת קישורים חיצוניים, סימני מסחר ו/או מוצרים של צד שלישי מופיע אך ורק כשירות למשתמש ואין בו משום תמיכה, חסות, המלצה, אישור דיוק, אמינות או חוקיות כלשהי מצד מפעילי הפלטפורמה.
          </Text>

          <Text style={styles.subsectionTitle}>11.3</Text>
          <Text style={styles.paragraph}>
            העדר אחריות לנזקים. מפעילי הפלטפורמה לא יהיו אחראים, במישרין או בעקיפין, לכל נזק או אובדן שיגרם או יטען על ידי המשתמש כתוצאה מהסתמכות על תוכן, טובין או שירותים של צד שלישי כאמור, או לכל עסקה שתתבצע בין המשתמש לבין אתרים אלה. מפעיל הפלטפורמה אינה אחראית לכל נזק או אובדן הנובע מתוכן או שירותים הזמינים באתרי אינטרנט או תכנים של צד שלישי.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>12.	אבטחת מידע</Text>
          <Text style={styles.paragraph}>
            המפעילים וספקיהם מיישמים מערכות, יישומים ונהלים כדי לאבטח את המידע של המשתמשים במטרה למזער את הסיכונים לגניבה, נזק, אובדן מידע או גישה או שימוש בלתי מורשים במידע. אמצעי אבטחת מידע שנעשים מספקים אבטחה סטנדרטית בתעשייה. יחד עם זאת, צוות הפלטפורמה אינו יכול להבטיח שמערכות הפלטפורמה יהיו חסינות מפני עוולות, תקלות, עיכובים, גישה לתכנים בלתי חוקיים, או סוגים אחרים של שימוש לרעה בפלטפורמה.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>13.	ניתוק, הפסקה ושינויים</Text>

          <Text style={styles.subsectionTitle}>13.1</Text>
          <Text style={styles.paragraph}>
            מפעילי הפלטפורמה רשאים לנתק ו/או להגביל ו/או להפסיק את אספקת השירות הניתן באמצעות הפלטפורמה, בכל עת, לרבות אך מבלי לגרוע, לצורך ביצוע פעולות תחזוקה או הקמה חיונית או מיידית במערכות המשמשות אותה.
          </Text>

          <Text style={styles.subsectionTitle}>13.2</Text>
          <Text style={styles.paragraph}>
            אין בחסימה, בהפסקה או בהגבלת השימוש בפלטפורמה, כאמור לעיל, כדי להטיל על מפעילי הפלטפורמה אחריות כלשהי ו/או כדי לגרוע מחובתו של המשתמש לנהוג בכל עת בהתאם לתנאי הסכם זה.
          </Text>

          <Text style={styles.subsectionTitle}>13.3</Text>
          <Text style={styles.paragraph}>
            מפעילי הפלטפורמה יוכלו לשנות מעת לעת את מבנה הפלטפורמה, את השירותים ו/או המוצרים, והמידע המופיעים בה, היקפם וזמינותם, המידע, וכל היבט אחר הכרוך בהם, והכל ללא צורך להודיע למשתמש. 
          </Text>

          <Text style={styles.subsectionTitle}>13.4</Text>
          <Text style={styles.paragraph}>
            למשתמש לא תהיה טענה, תביעה ו/או דרישה כלפי המפעילים ו/או מי טעמם ו/או מי ממנהלי הפלטפורמה ו/או מי מעובדיה ו/או מי מטעמם בגין ביצוע שינויים כאמור בסעיף זה, על כל תתי סעיפיו ו/או תקלות העלולות להתרחש אגב ביצועם.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>14.	הדין החל וסמכות שיפוט</Text>
          <Text style={styles.paragraph}>
            	על השימוש בפלטפורמה ועל תנאי השימוש יחולו אך ורק חוקי מדינת ישראל מבלי לתת תוקף לדיניה הנוגעים לניגוד דינים ולבתי המשפט המוסמכים של תל-אביב תהיה סמכות שיפוט בלעדית בכל מחלוקת בקשר עם התחייבות זו.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>15.	פרטי יצירת קשר</Text>
          <Text style={styles.paragraph}>
            בכל שאלה או חשש לגבי תנאים אלו או השירות, ניתן לפנות אלינו באמצעות דואר אלקטרוני: kursi.online.il@gmail.com 
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>16.	כללי</Text>
          <Text style={styles.paragraph}>
            תנאי שימוש אלה מהווה את כל ההסכם בין הצדדים ומחליף כל משא ומתן או הסכמים קודמים או בו-זמניים, בין בעל פה ובין בכתב, הקשורים לנושא זה. המשתמש אינו מסתמך על מצג כלשהו הנוגע לנושא זה, בעל פה או בכתב, שאינו כלול בתנאי שימוש אלה. שום מצג, הבטחה או תמריץ שאינם כלולים בתנאי שימוש אינם מחייבים.
          </Text>
        </View>
      </ScrollView>

      <AccessibilityButton />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.md,
    paddingTop: Platform.OS === 'ios' ? 50 : theme.spacing.lg,
    backgroundColor: theme.colors.backgroundLight,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  headerTitle: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  placeholder: {
    width: 40,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: theme.spacing.lg,
    paddingBottom: theme.spacing.xl * 2,
  },
  title: {
    fontSize: 28,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  updateDate: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    textAlign: 'center',
    marginBottom: theme.spacing.xl,
  },
  section: {
    marginBottom: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: 20,
    fontFamily: theme.fonts.bold,
    color: theme.colors.primary,
    marginBottom: theme.spacing.md,
    textAlign: 'right',
  },
  subsectionTitle: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: theme.colors.primary,
    marginBottom: theme.spacing.xs,
    textAlign: 'right',
  },
  paragraph: {
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    lineHeight: 24,
    textAlign: 'right',
    marginBottom: theme.spacing.sm,
  },
  listItem: {
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    lineHeight: 24,
    textAlign: 'right',
    marginBottom: theme.spacing.xs,
    paddingRight: theme.spacing.sm,
  },
});